import { FC, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  toggleControlBarShowing,
  updateControlBarComponents,
} from '../../../../../redux/ui/controlbar/actions';
import { useNavigate } from 'react-router-dom';
import BackToOverviewBtn from '../../components/buttons/BackToOverviewBtn';
import { RaidrV2RoutePaths } from '../../../../../routes/page-routes';
import TokenService from '../../../../../services/token-service';
import useRouteParams from '../../../../../hooks/useRouteParams';
import CompareVersionSelector from '../components/CompareVersionSelector';
import { clientNameSelector } from '../../../../../redux/auth/selectors';
import { useFundAndShareClassName } from '../hooks/useFundAndShareClassName';
import { useSelector } from 'react-redux';
import { KidShareClass } from '../types/KidEditorV2.types';
import KidsComparisonView from './KidsComparisonView';

const KidsCompareV2: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user_client_id: clientId } = TokenService.getUser()!;
  const clientName = useSelector(clientNameSelector);

  const fundId = useRouteParams('fundId')['fundId'] || '';
  const shareClassId = useRouteParams('shareClassId')['shareClassId'] || '';
  const documentLanguage =
    useRouteParams('documentLanguage')['documentLanguage'] || 'English';
  const compareVersion =
    useRouteParams('compareVersion')['compareVersion'] || '';
  const targetVersion = useRouteParams('targetVersion')['targetVersion'] || '';

  const { fundNameFull, shareClassName, isLoading } = useFundAndShareClassName({
    clientId,
    fundId,
    shareClassId,
    pageKey: 'kid-compare-v2',
  });

  const displayFundName = fundNameFull || (isLoading ? '…' : fundId);
  const displayShareClassName = shareClassName || (isLoading ? '…' : shareClassId);

  const shareClassForSelector: (KidShareClass & { fundID: string }) | null =
    shareClassId && documentLanguage
      ? {
          share_class_id: shareClassId,
          share_class_isin: shareClassId,
          version_number: targetVersion || compareVersion || '-',
          status: 'published' as KidShareClass['status'],
          kid_is_active: true,
          document_language: documentLanguage,
          fundID: fundId,
        }
      : null;

  const goToOverview = useCallback(() => {
    navigate(RaidrV2RoutePaths.KID_OVERVIEW_V2, { state: { fundId, shareClassId } });
  }, [navigate, fundId, shareClassId]);

  useEffect(() => {
    dispatch(
      updateControlBarComponents([
        <BackToOverviewBtn
          backToOverview={goToOverview}
          dontSetPosition
          key="back_to_overview"
        />,
        <div className="ml-4 flex min-w-0 flex-1 items-center gap-4 pr-2">
          <h6 className="truncate text-xl font-semibold text-mainblue">
            {displayFundName} - {displayShareClassName} (v{targetVersion} ↔ v{compareVersion})
          </h6>
          {shareClassForSelector && (
            <CompareVersionSelector
              shareClass={shareClassForSelector}
              clientId={clientId}
              fundId={fundId}
              initialTargetVersion={targetVersion || undefined}
              initialCompareVersion={compareVersion || undefined}
              className="text-base font-medium whitespace-nowrap w-fit"
            />
          )}
        </div>,
      ]),
    );
    dispatch(toggleControlBarShowing(true));

    return () => {
      dispatch(updateControlBarComponents([]));
      dispatch(toggleControlBarShowing(false));
    };
  }, [
    dispatch,
    goToOverview,
    displayFundName,
    displayShareClassName,
    compareVersion,
    targetVersion,
    clientId,
    fundId,
    shareClassId,
    documentLanguage,
  ]);

  return (
    <KidsComparisonView
      pageKey="kid-compare-v2"
      clientName={clientName ?? ''}
    />
  );
};

export default KidsCompareV2;
